// Main Application JavaScript - All-in-One Bundle
// All vendor libraries loaded via npm packages

// Import vendor libraries
import $ from 'jquery';
import * as bootstrap from 'bootstrap';
import { Swiper } from 'swiper/bundle';
import AOS from 'aos';
import 'aos/dist/aos.css';

// Bootstrap Icons - No JavaScript needed, pure CSS classes

// CSS is handled separately by webpack

// Import modules
import { initializeGalleryModal } from './modules/gallery-modal-optimized.js';
import { waitForPhoneInput } from './modules/phone-input.js';
import { initializeStatCounters } from './modules/stat-counters.js';
import { initializePortfolioFilter } from './modules/portfolio-filter.js';
import {
  initializeToastSystem,
  showToast,
} from './modules/toast-notifications.js';
import {
  initializeLazyLoading,
  addLazyLoadingStyles,
} from './modules/lazy-loading.js';
import { initializeLanguageBar } from './modules/language-bar.js';
import { initializeBackToTop } from './modules/back-to-top.js';
import AdvancedTracking from './modules/advanced-tracking.js';

// Import testimonials slider
import './testimonials-slider-flawless.js';

// Expose libraries globally for compatibility
window.$ = window.jQuery = $;
window.bootstrap = bootstrap;
window.AOS = AOS;
window.Swiper = Swiper;

// All initialization handled directly in this file

// DOM Content Loaded

// Wait for all libraries to be available with timeout
function waitForLibraries() {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max wait

    const checkLibraries = () => {
      attempts++;

      if (window.bootstrap && window.AOS && window.Swiper && window.$) {
        console.log('All libraries loaded successfully');
        resolve();
      } else if (attempts >= maxAttempts) {
        console.warn('Libraries not loaded after timeout, continuing anyway');
        resolve(); // Continue even if libraries aren't loaded
      } else {
        setTimeout(checkLibraries, 100);
      }
    };
    checkLibraries();
  });
}

// Use requestIdleCallback for non-critical initialization
function initializeWhenIdle() {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(initializeApp, { timeout: 2000 });
  } else {
    setTimeout(initializeApp, 100);
  }
}

// Critical features that need to work immediately
function initializeCriticalFeatures() {
  // Mobile menu functionality (critical for mobile users)
  const navbarToggler = document.querySelector('.navbar-toggler');
  const navbarCollapse = document.querySelector('.navbar-collapse');

  if (navbarToggler && navbarCollapse) {
    navbarToggler.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();

      if (navbarCollapse.classList.contains('show')) {
        navbarCollapse.classList.remove('show');
        navbarToggler.setAttribute('aria-expanded', 'false');
      } else {
        navbarCollapse.classList.add('show');
        navbarToggler.setAttribute('aria-expanded', 'true');
      }
    });
  }
}

async function initializeApp() {
  // Wait for all libraries to be available
  await waitForLibraries();
  // Get mobile menu elements first
  const navbarToggler = document.querySelector('.navbar-toggler');
  const navbarCollapse = document.querySelector('.navbar-collapse');

  // Initialize Bootstrap dropdowns
  const dropdownElementList = document.querySelectorAll(
    '[data-bs-toggle="dropdown"]'
  );

  dropdownElementList.forEach(function (dropdownToggleEl) {
    try {
      if (window.bootstrap && window.bootstrap.Dropdown) {
        console.log('Initializing Bootstrap Dropdown...');
        // Initialize dropdown with proper options
        new window.bootstrap.Dropdown(dropdownToggleEl, {
          autoClose: true,
          boundary: 'viewport',
        });

        // Handle dropdown click - close mobile menu if open
        dropdownToggleEl.addEventListener('click', function () {
          if (navbarCollapse && navbarCollapse.classList.contains('show')) {
            navbarCollapse.classList.remove('show');
            if (navbarToggler) {
              navbarToggler.setAttribute('aria-expanded', 'false');
            }
          }
        });
      }
    } catch {
      // Error initializing dropdown
    }
  });

  // Initialize all tooltips
  const tooltipTriggerList = document.querySelectorAll(
    '[data-bs-toggle="tooltip"]'
  );
  tooltipTriggerList.forEach(function (tooltipTriggerEl) {
    try {
      if (window.bootstrap && window.bootstrap.Tooltip) {
        console.log('Initializing Bootstrap Tooltip...');
        new window.bootstrap.Tooltip(tooltipTriggerEl);
      }
    } catch {
      // Error initializing tooltip
    }
  });

  // Initialize all modals
  const modalList = document.querySelectorAll('.modal');
  modalList.forEach(function (modalEl) {
    try {
      if (window.bootstrap && window.bootstrap.Modal) {
        console.log('Initializing Bootstrap Modal...');
        new window.bootstrap.Modal(modalEl);
      }
    } catch {
      // Error initializing modal
    }
  });

  // Bootstrap Icons are handled via CSS classes - no JavaScript needed

  // Initialize mobile menu functionality
  if (navbarToggler && navbarCollapse) {
    // Simple toggle without Bootstrap Collapse instance
    navbarToggler.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();

      // Toggle the show class manually
      if (navbarCollapse.classList.contains('show')) {
        navbarCollapse.classList.remove('show');
        navbarToggler.setAttribute('aria-expanded', 'false');
      } else {
        navbarCollapse.classList.add('show');
        navbarToggler.setAttribute('aria-expanded', 'true');
      }
    });

    // Close mobile menu when clicking on nav links
    const navLinks = document.querySelectorAll(
      '.navbar-nav .nav-link, .navbar .btn'
    );
    navLinks.forEach(link => {
      link.addEventListener('click', function () {
        if (navbarCollapse.classList.contains('show')) {
          navbarCollapse.classList.remove('show');
          navbarToggler.setAttribute('aria-expanded', 'false');
        }
      });
    });

    // Close mobile menu when clicking outside
    document.addEventListener('click', function (e) {
      if (
        !e.target.closest('.navbar') &&
        navbarCollapse.classList.contains('show')
      ) {
        navbarCollapse.classList.remove('show');
        navbarToggler.setAttribute('aria-expanded', 'false');
      }
    });

    // Close mobile menu on escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && navbarCollapse.classList.contains('show')) {
        navbarCollapse.classList.remove('show');
        navbarToggler.setAttribute('aria-expanded', 'false');
      }
    });
  }

  // Initialize AOS
  if (typeof window.AOS !== 'undefined' && window.AOS.init) {
    console.log('Initializing AOS...');
    window.AOS.init({
      duration: 1000,
      easing: 'ease-out-cubic',
      once: false,
      offset: 100,
      delay: 100,
    });
    console.log('AOS initialized successfully');
  } else {
    console.error('AOS not available');
  }

  // Initialize sliders with lazy loading for better performance
  // Hero Slider - Initialize after a short delay to improve FCP
  setTimeout(() => {
    const heroSlider = document.querySelector('.hero-slider');
    if (heroSlider && typeof window.Swiper !== 'undefined') {
      console.log('Initializing Hero Swiper...');
      // Images are now handled by <picture> elements, no need to set backgrounds
      // Just ensure slides are properly styled
      const heroSlides = document.querySelectorAll('.hero-slide');
      heroSlides.forEach(slide => {
        slide.classList.add('bg-loaded');
      });

      // Initialize Swiper with optimized settings
      new window.Swiper(heroSlider, {
        loop: true,
        autoplay: {
          delay: 5000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        },
        effect: 'fade',
        fadeEffect: {
          crossFade: true,
        },
        navigation: {
          nextEl: '.hero-next',
          prevEl: '.hero-prev',
        },
        pagination: {
          el: '.hero-pagination',
          clickable: true,
        },
        speed: 800, // Faster transition
        allowTouchMove: true,
        simulateTouch: true,
        watchSlidesProgress: true,
      });
    }
  }, 100); // Small delay to improve FCP

  // Wellness Slider
  const wellnessSlider = document.querySelector('.wellness-swiper');
  if (wellnessSlider && typeof window.Swiper !== 'undefined') {
    console.log('Initializing Wellness Swiper...');
    new window.Swiper(wellnessSlider, {
      slidesPerView: 1,
      spaceBetween: 30,
      loop: true,
      autoplay: {
        delay: 4000,
        disableOnInteraction: false,
      },
      navigation: {
        nextEl: '.wellness-next',
        prevEl: '.wellness-prev',
      },
      pagination: {
        el: '.wellness-pagination',
        clickable: true,
      },
      breakpoints: {
        768: { slidesPerView: 2, spaceBetween: 30 },
        1024: { slidesPerView: 3, spaceBetween: 40 },
      },
    });
  }

  // Testimonials slider is handled by the flawless slider module
  // No additional initialization needed here

  // Initialize back to top button
  initializeBackToTop();

  // Advanced tracking is initialized in advanced-tracking.js
  // No duplicate initialization needed here

  // Initialize all modules with error handling
  try {
    addLazyLoadingStyles();
  } catch {
    // Error initializing lazy loading styles
  }

  try {
    initializeLazyLoading();
  } catch {
    // Error initializing lazy loading
  }

  try {
    initializeLanguageBar();
  } catch {
    // Error initializing language bar
  }

  try {
    initializeToastSystem();
  } catch {
    // Error initializing toast system
  }

  try {
    initializeStatCounters();
  } catch {
    // Error initializing stat counters
  }

  try {
    initializePortfolioFilter();
  } catch {
    // Error initializing portfolio filter
  }

  try {
    initializeGalleryModal();
  } catch {
    // Error initializing gallery modal
  }

  try {
    waitForPhoneInput();
  } catch {
    // Error initializing phone input
  }

  // Form handling will be initialized separately
}

// Form Initialization - All form handling is now done by advanced-tracking.js
function initializeForms() {
  // Forms are automatically handled by advanced-tracking.js module
  // No duplicate form handling needed here
  console.log('Forms will be handled by advanced-tracking.js');
}

// Toast function is now imported from modules

// Tracking data is now handled by advanced-tracking.js module

// All tracking is now handled by advanced-tracking.js module

// Form State Management Functions are now handled by advanced-tracking.js

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function () {
  // Initialize critical features immediately
  initializeCriticalFeatures();

  // Initialize forms immediately (critical for user interaction)
  initializeForms();

  // Initialize non-critical features when browser is idle
  initializeWhenIdle();
});
